/**
 * 74.10 step 1 (DECISIONS 362, ARCHITECTURE §5.45): the air per world — one profile, `airOf(planet)`, in place of the
 * four tables the drawing kept (the rim's tint, the sky's colour and strength, the sky's gradient, the rim at a fixed
 * 1.07 R). Pure, no Three.js: the rendering reads it (rendering/skyHaze.ts, renderer.ts, cloudLayer.ts), and the sim
 * reads `density` behind 74.10 step 3's switch (rotorcraft refused on thin air). Nothing here runs per tick.
 *
 * - **Density** is the type's, scaled by the world's size (the owner, 362: "by type, scaled by size"): 0 on a tiny world
 *   (bare space, as 67.40 drew it), rising over the small and medium classes to the type's full value on a large world.
 *   1 is a temperate world; thin is under `AIR.rotorMin` (0.3), where rotors find nothing to bite.
 * - **The rim** stands over each world's summits (362: "the rim over the summits"): `AIR.rimMin` (0.07 R, the old rim)
 *   or the highest ground plus `AIR.rimOverSummit`, whichever is higher — so no ridge walks out of the air into the shell
 *   (the sheets of 361), and a rugged world wears a taller air from orbit. The summit is read from the generated heightmap
 *   once per array (a crater digs down; a mound stays under the clamp).
 * - **The colours** are the type's: the rim's tint and glow (the Fresnel shell), the sky's colour and strength (the dome's
 *   thickness, scaled with the density), the gradient (zenith, horizon, the warm side and how far it warms, 67.40).
 */
import type { PlanetState, PlanetArchetype } from '../types';
import { SIZE_CATEGORY_RADIUS } from '../constants/world';
import { dexp } from './dmath';

/** One archetype's air. `rim.glow` is the shell's full intensity; `sky.strength` the dome's opacity on a large world's ground. */
export interface AirType {
  /** the air's thickness on a large world: 1 temperate, under 0.3 thin, over 1 thick */
  density: number;
  rim: { color: string; glow: number };
  sky: { color: string; strength: number };
  look: { zenith: string; horizon: string; warm: string; warmth: number };
}

/** The air by archetype (earth is thalia's palette with thalia's air). The rim colours are the old table's, or the palette's average where it had none. */
export const AIR_TYPES: Record<PlanetArchetype | 'earth', AirType> = {
  thalia:  { density: 1.0,  rim: { color: '#81ad85', glow: 0.32 }, sky: { color: '#9cc4de', strength: 0.78 }, look: { zenith: '#4f8cc8', horizon: '#bcd8ec', warm: '#f2c08a', warmth: 0.55 } },
  earth:   { density: 1.0,  rim: { color: '#81ad85', glow: 0.32 }, sky: { color: '#9cc4de', strength: 0.78 }, look: { zenith: '#4f8cc8', horizon: '#bcd8ec', warm: '#f2c08a', warmth: 0.55 } },
  pelago:  { density: 1.0,  rim: { color: '#85a9a5', glow: 0.32 }, sky: { color: '#92c2d6', strength: 0.78 }, look: { zenith: '#3e86b4', horizon: '#b2d8e2', warm: '#f0c492', warmth: 0.6 } },
  bruma:   { density: 0.9,  rim: { color: '#6090d0', glow: 0.48 }, sky: { color: '#c4d2e2', strength: 0.72 }, look: { zenith: '#7e9cc6', horizon: '#dce6f0', warm: '#f6b88a', warmth: 0.75 } },
  viridis: { density: 1.2,  rim: { color: '#7c907c', glow: 0.32 }, sky: { color: '#a8d2b4', strength: 0.72 }, look: { zenith: '#5e9e86', horizon: '#c4e2c8', warm: '#ecd08c', warmth: 0.45 } },
  murex:   { density: 0.8,  rim: { color: '#9060c0', glow: 0.40 }, sky: { color: '#b6a6cc', strength: 0.62 }, look: { zenith: '#5a4a86', horizon: '#c4b0d8', warm: '#f0a090', warmth: 0.6 } },
  sonora:  { density: 0.7,  rim: { color: '#c8c8d8', glow: 0.30 }, sky: { color: '#c4b4dc', strength: 0.62 }, look: { zenith: '#7466a8', horizon: '#d6cce8', warm: '#f4b4a0', warmth: 0.6 } },
  ochrus:  { density: 0.6,  rim: { color: '#a98c78', glow: 0.32 }, sky: { color: '#dcbc94', strength: 0.58 }, look: { zenith: '#b08a5e', horizon: '#ecd2ac', warm: '#ffd090', warmth: 0.25 } },
  tephra:  { density: 0.8,  rim: { color: '#d08040', glow: 0.56 }, sky: { color: '#a89a94', strength: 0.58 }, look: { zenith: '#6e6460', horizon: '#bcaea6', warm: '#e89060', warmth: 0.4 } },
  pallor:  { density: 0.5,  rim: { color: '#a9a985', glow: 0.32 }, sky: { color: '#cfc6ae', strength: 0.5 },  look: { zenith: '#a8a294', horizon: '#e6dece', warm: '#f4d0a0', warmth: 0.35 } },
};

export const AIR = {
  /** the rim's least height over the sphere (fraction of R): the old fixed rim */
  rimMin: 0.07,
  /** the rim stands this much over the highest ground (fraction of R) */
  rimOverSummit: 0.03,
  /** under this radius a world holds no air (the tiny class, 67.40) */
  airlessUnder: SIZE_CATEGORY_RADIUS.tiny[1],
  /** the size scaling of the density: this factor at the airless edge, rising to 1 by `fullR` (the medium class: every world the Lab plays today is full) */
  thinFactor: 0.4,
  fullR: SIZE_CATEGORY_RADIUS.medium[0],
  /** under this density rotors find nothing to bite (74.10 step 3's rule) */
  rotorMin: 0.3,
} as const;

/** The archetype's air (earth's palette takes its own row, the same as thalia's). */
export function airTypeOf(planet: Pick<PlanetState, 'archetype' | 'palette'>): AirType {
  return (planet.palette?.name === 'earth' ? AIR_TYPES.earth : undefined) ?? AIR_TYPES[planet.archetype as PlanetArchetype] ?? AIR_TYPES.thalia;
}

/** The density's size factor: 0 under the airless radius, `thinFactor` at it, 1 from `fullR` out. */
export function sizeFactor(radius: number): number {
  if (!(radius >= AIR.airlessUnder)) return 0;
  const t = Math.min(1, (radius - AIR.airlessUnder) / (AIR.fullR - AIR.airlessUnder));
  return AIR.thinFactor + (1 - AIR.thinFactor) * t;
}

const summits = new WeakMap<Float32Array, number>();
/** The highest ground over the sphere (fraction of R), read once per heightmap array. */
export function summitOf(planet: Pick<PlanetState, 'heightmap' | 'radius'>): number {
  const h = planet.heightmap;
  const known = summits.get(h);
  if (known !== undefined) return known;
  let max = 0;
  for (let i = 0; i < h.length; i++) if (h[i] > max) max = h[i];
  const s = planet.radius > 0 ? max / planet.radius : 0;
  summits.set(h, s);
  return s;
}

export interface AirProfile {
  type: AirType;
  /** the type's density × the size factor; 0 is airless */
  density: number;
  airless: boolean;
  /** the rim's radius as a fraction of R: over the summits */
  rimOverR: number;
  /** the highest ground (fraction of R) */
  summit: number;
  /** the dome's opacity on the ground: the type's strength, scaled with the density */
  strength: number;
}

/** A world's air. */
export function airOf(planet: Pick<PlanetState, 'archetype' | 'palette' | 'radius' | 'heightmap'>): AirProfile {
  const type = airTypeOf(planet);
  const f = sizeFactor(planet.radius);
  const summit = summitOf(planet);
  return { type, density: type.density * f, airless: f <= 0, rimOverR: 1 + Math.max(AIR.rimMin, summit + AIR.rimOverSummit), summit, strength: type.sky.strength * f };
}

/**
 * 74.10 step 2 (362: "sunsets on the sky and the key light"): the sun through the air. Per unit of density and of air mass
 * the air takes this much of each channel (blue most, as the sky is blue), so a low sun comes through red and dim —
 * the more air, the redder and the dimmer. Relative to the sun overhead (air mass 1), so a high sun on any world lights
 * as it did before this.
 */
export const SUN_TAU = { r: 0.05, g: 0.10, b: 0.20 } as const;
/** The sun's elevation (its sine) the extinction stops thickening at: under it the sun is behind the ground anyway. */
export const SUN_ELEVATION_FLOOR = 0.08;
export interface SunTint { r: number; g: number; b: number }
/** The sun's transmission through the air (each channel 0–1, 1 overhead) at `sinElevation` (up · sun) in air of `density`. */
export function sunExtinction(sinElevation: number, density: number): SunTint {
  if (!(density > 0)) return { r: 1, g: 1, b: 1 };
  const airmass = 1 / Math.max(SUN_ELEVATION_FLOOR, Math.min(1, sinElevation));
  const x = density * (airmass - 1);
  return { r: dexp(-SUN_TAU.r * x), g: dexp(-SUN_TAU.g * x), b: dexp(-SUN_TAU.b * x) };
}
/** The tint's brightness (the key light's intensity factor): luminance. */
export function tintLuminance(t: SunTint): number {
  return 0.2126 * t.r + 0.7152 * t.g + 0.0722 * t.b;
}
/** 67.40's dusk, sized by the air: how much of dusk it is with the sun at `sinElevation` — thick air a longer, stronger dusk; thin a short faint one; density 1 as before. */
export const DUSK = { centre: 0.02, width: 0.16 } as const;
export function duskOf(sinElevation: number, density: number): number {
  const width = DUSK.width * (0.5 + 0.5 * density), amount = 0.6 + 0.4 * density;
  const x = (sinElevation - DUSK.centre) / width;
  return amount * dexp(-x * x);
}

/** Whether rotors can fly in this air (74.10 step 3's rule, read behind its switch). */
export function rotorsFly(air: Pick<AirProfile, 'density'>): boolean {
  return air.density >= AIR.rotorMin;
}
